import { useState } from 'react';
import { createDefaultSnapshot, MAX_REQUESTS, RequestSnapshot } from './serviceRequestModel';

interface RequestTabsInput {
  captureSnapshot: () => RequestSnapshot;
  restoreSnapshot: (snapshot: RequestSnapshot) => void;
}

/**
 * The "another request" tabs: each is a snapshot of the form, captured on leaving it and restored on return.
 * At most MAX_REQUESTS tabs, and a submitted tab cannot be removed.
 */
export function useRequestTabs({ captureSnapshot, restoreSnapshot }: RequestTabsInput) {
  const [requests, setRequests] = useState<RequestSnapshot[]>(() => [createDefaultSnapshot()]);
  const [activeTab, setActiveTab] = useState(0);
  const [anotherRequest, setAnotherRequest] = useState(false);

  const revealTab = (snapshots: RequestSnapshot[], index: number) => {
    if (index < 0 || index === activeTab) return;
    setActiveTab(index);
    restoreSnapshot(snapshots[index]);
  };

  const handleContinue = () => {
    if (requests.length >= MAX_REQUESTS) return;
    const snapshot = captureSnapshot();
    const updatedRequests = [...requests];
    updatedRequests[activeTab] = snapshot;
    const newSnapshot: RequestSnapshot = {
      ...snapshot,
      schemaFormErrors: [],
      attachments: [],
      opportunityId: null,
      status: 'pending'
    };
    updatedRequests.push(newSnapshot);
    const newTab = updatedRequests.length - 1;
    setRequests(updatedRequests);
    setActiveTab(newTab);
    setAnotherRequest(false);
    restoreSnapshot(newSnapshot);
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    if (newValue === activeTab) return;
    const snapshot = captureSnapshot();
    const updatedRequests = [...requests];
    updatedRequests[activeTab] = snapshot;
    setRequests(updatedRequests);
    setActiveTab(newValue);
    restoreSnapshot(updatedRequests[newValue]);
    setAnotherRequest(false);
  };

  const handleRemoveTab = (index: number) => {
    if (requests.length <= 1 || requests[index].status === 'success') return;
    const withCurrent = [...requests];
    withCurrent[activeTab] = captureSnapshot();
    const updatedRequests = withCurrent.filter((_, i) => i !== index);
    const newActiveTab = activeTab >= index && activeTab > 0 ? activeTab - 1 : activeTab;
    setRequests(updatedRequests);
    setActiveTab(newActiveTab);
    restoreSnapshot(updatedRequests[newActiveTab]);
    setAnotherRequest(false);
  };

  const resetTabs = () => {
    setRequests([createDefaultSnapshot()]);
    setActiveTab(0);
    setAnotherRequest(false);
  };

  return {
    requests,
    setRequests,
    activeTab,
    anotherRequest,
    setAnotherRequest,
    revealTab,
    handleContinue,
    handleTabChange,
    handleRemoveTab,
    resetTabs
  };
}
